import { useMemo } from 'react'
import { multiEditConfig } from '@editorUtils'
import { find } from 'lodash-es'
import { useModelStore } from '@editorStores'

type MultiEditConfig = ReturnType<typeof multiEditConfig>

/**
 * Maps the current multi-edit selection onto the member guids that carry check
 * settings.
 *
 * Line-like positions and the Decke/Dach lists already select member guids, so
 * they pass through unchanged — rips and lintels come along here and are sorted
 * out per section by the element-type visibility predicates. A scene selection
 * of slab surfaces holds no member guids at all and is mapped to each surface's
 * beam guid instead. Selections that hold no members (walls, openings, mixed)
 * yield an empty list.
 */
const useMultiEditMemberGuids = (
  selectedElements: string[],
  config: MultiEditConfig | null,
): string[] => {
  const model = useModelStore(state => state.model)

  return useMemo(() => {
    if (!config) return []

    const { onlyVerticalSlabs, onlyVerticalRoofSlabs, onlySlabAndRoofBeams, onlyLineLike } = config

    if (onlySlabAndRoofBeams || onlyLineLike) return selectedElements

    if (!onlyVerticalSlabs && !onlyVerticalRoofSlabs) return []

    const slabs = onlyVerticalSlabs ? model.vertical_slabs : model.vertical_roof_slabs

    return selectedElements
      .map(guid => find(slabs, { guid })?.beam?.guid)
      .filter((beamGuid): beamGuid is string => !!beamGuid)
  }, [config, model.vertical_slabs, model.vertical_roof_slabs, selectedElements])
}

export default useMultiEditMemberGuids
